import { useState, useMemo, useCallback } from "react";
import {
  getAncestors,
  getDescendants,
  isNewServiceCategory,
} from "../../utils/serviceCategoryV2Mapping";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export const STATUS_DOT_COLORS = {
  Sent: "bg-blue-600",
  "Not Sent": "bg-gray-400",
  "In Progress": "bg-yellow-400",
  "In Review": "bg-purple-400",
  Complete: "bg-green-400",
  "In Remediation (Purchaser)": "bg-red-500",
  "In Remediation (Vendor)": "bg-orange-500",
  Cancelled: "bg-red-600",
  Archived: "bg-slate-400",
  Released: "bg-emerald-500",
  Pending: "bg-amber-400",
  Published: "bg-blue-500",
  Upcoming: "bg-slate-400",
};

function getStatusDotColor(status) {
  return STATUS_DOT_COLORS[status] || "bg-gray-400";
}

/** Same logic as FiltersDropdown `filteredV2Options` for hierarchical Services. */
function filterV2OptionsByQuery(options, queryRaw) {
  if (!options?.length) return [];
  const q = (queryRaw || "").trim().toLowerCase();
  if (!q) return options;
  const matchingGroups = new Set();
  options.forEach((opt) => {
    if (!opt.isGroupHeader && String(opt.label || "").toLowerCase().includes(q)) {
      if (opt.groupName) matchingGroups.add(opt.groupName);
    }
  });
  return options.filter((opt) => {
    if (opt.isGroupHeader) return matchingGroups.has(opt.label);
    return String(opt.label || "").toLowerCase().includes(q);
  });
}

/**
 * Value to compare the selection (must match FiltersDropdown / API).
 */
export function defaultGetOptionValue(sectionId, option) {
  if (!option) return undefined;
  if (sectionId === "sponsor") return option.id;
  if (sectionId === "provider") return option.id;
  if (sectionId === "docType") return option.docId;
  if (sectionId === "status") return option.statusID;
  if (sectionId === "templates") return option.hash_id;
  /**
   * Library Requests "Scope" uses the same RFI catalog options as Document Library `services`
   * (`useRfiServiceFilterOptions`). Legacy template rows may only have hash_id/id.
   */
  if (sectionId === "scope") {
    return (
      option.rfiId ??
      option.rfi_id ??
      option.hash_id ??
      option.id ??
      option.value
    );
  }
  /** RFI catalog rows: match Document Library / FiltersDropdown resolution. */
  if (sectionId === "services") {
    return (
      option.rfiId ??
      option.rfi_id ??
      option.id ??
      option.hash_id ??
      option.value
    );
  }
  if (sectionId === "type" || sectionId === "gxp") return option.value;
  return option.value ?? option.id ?? option.hash_id ?? option.statusID;
}

/**
 * Search bar + popover filters (Innovation Lab RFI History).
 *
 * @param {object} props
 * @param {Array<{ id: string, name: string, searchable?: boolean, options?: array }>} props.sections
 * @param {object} props.selectedIds - e.g. { provider: [], status: [], templates: [] }
 * @param {function} props.setSelectedIds
 * @param {function} [props.getOptionValue] - (sectionId, option) => value
 * @param {Array} [props.serviceCategoryV2SourceData] - raw RFI catalog for V2 ancestor/descendant resolution (UseGetRfiListForFilters)
 */
export default function PageToolbarFilters({
  sections = [],
  selectedIds,
  setSelectedIds,
  getOptionValue = defaultGetOptionValue,
  serviceCategoryV2SourceData = null,
}) {
  const [open, setOpen] = useState(false);
  const [searchQueries, setSearchQueries] = useState({});

  const normalizedSections = useMemo(() => {
    return sections.map((section) => ({
      ...section,
      searchable: Boolean(section.searchable),
      options: section.options || [],
    }));
  }, [sections]);

  const getFilteredOptions = useCallback(
    (section) => {
      const raw = searchQueries[section.id] || "";
      if (section.isV2) {
        return filterV2OptionsByQuery(section.options || [], raw);
      }
      const query = raw.trim().toLowerCase();
      if (!query) return section.options;
      return section.options.filter((opt) => {
        const label =
          opt.label ?? opt.name ?? opt.services ?? opt.rfi_name ?? "";
        return String(label).toLowerCase().includes(query);
      });
    },
    [searchQueries]
  );

  const isSelected = useCallback(
    (sectionId, value) => {
      const list = selectedIds[sectionId];
      if (!Array.isArray(list) || value === undefined || value === null) {
        return false;
      }
      return list.includes(value);
    },
    [selectedIds]
  );

  const getActiveCount = useCallback(
    (sectionId) => selectedIds[sectionId]?.length || 0,
    [selectedIds]
  );

  const getSelectableValues = useCallback(
    (section) => {
      if (section.isV2) {
        return (section.options || [])
          .filter((o) => !o.isGroupHeader && o.rfiId != null)
          .map((o) => o.rfiId);
      }
      return section.options
        .map((opt) => getOptionValue(section.id, opt))
        .filter((v) => v !== undefined && v !== null);
    },
    [getOptionValue]
  );

  const isAllSelected = useCallback(
    (section) => {
      const all = getSelectableValues(section);
      const sel = selectedIds[section.id] || [];
      return all.length > 0 && all.every((v) => sel.includes(v));
    },
    [getSelectableValues, selectedIds]
  );

  const handleToggleAll = useCallback(
    (section) => {
      if (isAllSelected(section)) {
        setSelectedIds((prev) => {
          const next = { ...prev, [section.id]: [] };
          return next;
        });
      } else {
        const all = getSelectableValues(section);
        setSelectedIds((prev) => ({ ...prev, [section.id]: [...all] }));
      }
    },
    [getSelectableValues, isAllSelected, setSelectedIds]
  );

  const handleV2Checkbox = useCallback(
    (section, optionValue, checked) => {
      const v2NameToId = {};
      (section.options || []).forEach((o) => {
        if (!o.isGroupHeader && o.rfiId) v2NameToId[o.value] = o.rfiId;
      });
      const data = serviceCategoryV2SourceData;
      setSelectedIds((prev) => {
        let currentIds = prev[section.id] || [];
        if (optionValue.startsWith("__group__")) {
          const groupName = optionValue.replace("__group__", "");
          const memberNames =
            section.options
              ?.filter((o) => !o.isGroupHeader && o.groupName === groupName)
              ?.map((o) => o.value) || [];
          const memberIds = memberNames.map((n) => v2NameToId[n]).filter(Boolean);
          currentIds = checked
            ? [...new Set([...currentIds, ...memberIds])]
            : currentIds.filter((v) => !new Set(memberIds).has(v));
        } else if (checked) {
          const namesToAdd = [
            optionValue,
            ...getDescendants(optionValue, data),
            ...getAncestors(optionValue, data),
          ];
          const idsToAdd = namesToAdd.map((n) => v2NameToId[n]).filter(Boolean);
          currentIds = [...new Set([...currentIds, ...idsToAdd])];
        } else {
          const idToRemove = v2NameToId[optionValue];
          currentIds = currentIds.filter((v) => v !== idToRemove);
        }
        return { ...prev, [section.id]: currentIds };
      });
    },
    [serviceCategoryV2SourceData, setSelectedIds]
  );

  const handleCheckbox = useCallback(
    (sectionId, option, checked) => {
      const value = getOptionValue(sectionId, option);
      if (value === undefined || value === null) return;

      setSelectedIds((prev) => {
        const prevList = prev[sectionId] || [];
        const nextList = checked
          ? [...new Set([...prevList, value])]
          : prevList.filter((v) => v !== value);
        return { ...prev, [sectionId]: nextList };
      });
    },
    [getOptionValue, setSelectedIds]
  );

  const renderOptionLabel = (section, option) => {
    const text =
      option.label ?? option.name ?? option.services ?? option.rfi_name ?? "";
    if (section.id === "status") {
      const dot =
        STATUS_DOT_COLORS[option.statusID] || getStatusDotColor(text);
      return (
        <span className="inline-flex items-center">
          <span
            className={classNames(
              "h-2 w-2 rounded-full inline-block mr-2",
              dot
            )}
          />
          {text}
        </span>
      );
    }
    return text;
  };

  return (
    <div className="bg-white">
      <Dialog open={open} onClose={setOpen} className="relative z-40 sm:hidden">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-black bg-opacity-25 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />
        <div className="fixed inset-0 z-40 flex">
          <DialogPanel
            transition
            className="relative ml-auto flex h-full w-full max-w-xs transform flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl transition duration-300 ease-in-out data-[closed]:translate-x-full"
          >
            <div className="flex items-center justify-between px-4">
              <h2 className="text-lg font-medium text-gray-900">Filters</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon aria-hidden="true" className="h-6 w-6" />
              </button>
            </div>
            <div className="mt-4 overflow-y-auto flex-1">
              {normalizedSections.map((section) => {
                const filteredOptions = getFilteredOptions(section);
                const v2NameToId = section.isV2
                  ? Object.fromEntries(
                      (section.options || [])
                        .filter((o) => !o.isGroupHeader && o.rfiId)
                        .map((o) => [o.value, o.rfiId])
                    )
                  : null;
                return (
                  <Disclosure key={section.name} as="div" className=" px-4 py-6">
                    <h3 className="-mx-2 -my-3 flow-root">
                      <DisclosureButton className="group flex w-full items-center justify-between bg-white px-2 py-3 text-sm text-gray-400">
                        <span className="font-medium text-gray-900">
                          {section.name}
                        </span>
                        <span className="ml-6 flex items-center">
                          <ChevronDownIcon
                            aria-hidden="true"
                            className="h-5 w-5 rotate-0 transform group-data-[open]:-rotate-180"
                          />
                        </span>
                      </DisclosureButton>
                    </h3>
                    <DisclosurePanel className="pt-6">
                      {section.searchable && (
                        <div className="relative text-gray-600 mb-4">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-1">
                            <MagnifyingGlassIcon
                              aria-hidden="true"
                              className="h-4 w-4"
                            />
                          </div>
                          <input
                            id={`search-mobile-${section.id}`}
                            type="search"
                            placeholder="Search"
                            value={searchQueries[section.id] || ""}
                            onChange={(e) =>
                              setSearchQueries((prev) => ({
                                ...prev,
                                [section.id]: e.target.value,
                              }))
                            }
                            className="block w-full rounded-md border bg-white py-1.5 pl-6 pr-3 focus:bg-white focus:text-gray-900 focus:ring-0 focus:placeholder:text-gray-500 sm:text-sm sm:leading-6"
                          />
                        </div>
                      )}
                      <div className="mb-3 pb-2 border-b border-gray-100">
                        <button
                          type="button"
                          onClick={() => handleToggleAll(section)}
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          {isAllSelected(section) ? "Unselect All" : "Select All"}
                        </button>
                      </div>
                      <div className="space-y-2.5 max-h-[26rem] overflow-y-auto overflow-x-hidden pr-1">
                        {section.isV2
                          ? filteredOptions.map((option, optionIdx) => {
                              if (option.isGroupHeader) {
                                const groupMembers = option.groupMembers || [];
                                const selectedInGroup = groupMembers.filter(
                                  (m) =>
                                    selectedIds[section.id]?.includes(
                                      v2NameToId[m]
                                    )
                                );
                                const allSelected =
                                  groupMembers.length > 0 &&
                                  selectedInGroup.length === groupMembers.length;
                                const someSelected =
                                  selectedInGroup.length > 0 && !allSelected;
                                return (
                                  <div
                                    key={`m-v2-grp-${section.id}-${optionIdx}`}
                                    className="pt-3 pb-1 first:pt-0"
                                  >
                                    <span
                                      className={`text-[11px] font-semibold uppercase tracking-wider select-none cursor-pointer transition-colors ${allSelected || someSelected ? "text-indigo-600" : "text-gray-400 hover:text-gray-600"}`}
                                      onClick={() =>
                                        handleV2Checkbox(
                                          section,
                                          option.value,
                                          !allSelected
                                        )
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                          e.preventDefault();
                                          handleV2Checkbox(
                                            section,
                                            option.value,
                                            !allSelected
                                          );
                                        }
                                      }}
                                      role="button"
                                      tabIndex={0}
                                    >
                                      {option.label}
                                    </span>
                                  </div>
                                );
                              }
                              const indentPx = (option.depth || 0) * 16;
                              const showNew = isNewServiceCategory(
                                option.value,
                                serviceCategoryV2SourceData
                              );
                              const isChecked =
                                selectedIds[section.id]?.includes(option.rfiId) ||
                                false;
                              return (
                                <div
                                  key={`m-v2-${section.id}-${optionIdx}`}
                                  className="flex items-start gap-3"
                                  style={{ paddingLeft: `${indentPx}px` }}
                                >
                                  <input
                                    value={option.value}
                                    checked={isChecked}
                                    onChange={(e) =>
                                      handleV2Checkbox(
                                        section,
                                        option.value,
                                        e.target.checked
                                      )
                                    }
                                    id={`filter-mobile-${section.id}-${optionIdx}`}
                                    name={`${section.id}[]`}
                                    type="checkbox"
                                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                  />
                                  <label
                                    htmlFor={`filter-mobile-${section.id}-${optionIdx}`}
                                    className="text-sm font-medium text-gray-900 leading-relaxed cursor-pointer flex-1"
                                  >
                                    {option.label}
                                    {showNew && (
                                      <span className="ml-1.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold text-violet-600 ring-1 ring-inset ring-violet-400/60 align-middle">
                                        New
                                      </span>
                                    )}
                                  </label>
                                </div>
                              );
                            })
                          : filteredOptions.map((option, optionIdx) => {
                              const value = getOptionValue(section.id, option);
                              return (
                                <div
                                  key={`m-${section.id}-${optionIdx}-${value}`}
                                  className="flex items-start gap-3"
                                >
                                  <div className="group grid size-4 grid-cols-1 mt-0.5 flex-shrink-0">
                                    <input
                                      value={value}
                                      checked={isSelected(section.id, value)}
                                      onChange={(e) =>
                                        handleCheckbox(
                                          section.id,
                                          option,
                                          e.target.checked
                                        )
                                      }
                                      id={`filter-mobile-${section.id}-${optionIdx}`}
                                      name={`${section.id}[]`}
                                      type="checkbox"
                                      className="col-start-1 row-start-1 size-4 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                    />
                                    <svg
                                      viewBox="0 0 16 16"
                                      fill="white"
                                      className="pointer-events-none col-start-1 row-start-1 size-4 opacity-0 group-has-[input:checked]:opacity-100"
                                    >
                                      <path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z" />
                                    </svg>
                                  </div>
                                  <label
                                    htmlFor={`filter-mobile-${section.id}-${optionIdx}`}
                                    className="text-sm text-gray-500 break-words cursor-pointer flex-1"
                                  >
                                    {renderOptionLabel(section, option)}
                                  </label>
                                </div>
                              );
                            })}
                      </div>
                    </DisclosurePanel>
                  </Disclosure>
                );
              })}
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      <div className="bg-white pb-2">
        <div className="flex items-center justify-end sm:justify-end">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="inline-block text-sm font-medium text-gray-700 hover:text-gray-900 sm:hidden"
            >
              Filters
            </button>
            <div className="hidden sm:block w-full sm:w-auto">
              <div className="flow-root">
                <PopoverGroup className="-mx-4 flex items-center divide-x divide-gray-200">
                  {normalizedSections.map((section) => {
                    const filteredOptions = getFilteredOptions(section);
                    const activeCount = getActiveCount(section.id);
                    const v2NameToId = section.isV2
                      ? Object.fromEntries(
                          (section.options || [])
                            .filter((o) => !o.isGroupHeader && o.rfiId)
                            .map((o) => [o.value, o.rfiId])
                        )
                      : null;
                    return (
                      <Popover
                        key={section.id}
                        className="relative inline-block px-4 text-left"
                      >
                        <PopoverButton
                          className={`group inline-flex justify-center text-sm font-medium ${activeCount > 0 ? "text-indigo-700" : "text-gray-700"} hover:text-gray-900`}
                        >
                          <span>{section.name}</span>
                          {activeCount > 0 && (
                            <span className="ml-1.5 rounded bg-indigo-100 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-indigo-700">
                              {activeCount}
                            </span>
                          )}
                          <ChevronDownIcon
                            aria-hidden="true"
                            className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                          />
                        </PopoverButton>
                        <PopoverPanel
                          transition
                          className="absolute right-0 z-10 mt-2 origin-top-right rounded-md bg-white p-4 shadow-2xl ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in w-80 max-h-96 overflow-hidden flex flex-col"
                        >
                          <div className="flex flex-col min-h-0 flex-1">
                            {section.searchable && (
                              <div className="relative text-gray-600 mb-4 shrink-0">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-1">
                                  <MagnifyingGlassIcon
                                    aria-hidden="true"
                                    className="h-4 w-4"
                                  />
                                </div>
                                <input
                                  id={`search-${section.id}`}
                                  type="search"
                                  placeholder="Search"
                                  value={searchQueries[section.id] || ""}
                                  onChange={(e) =>
                                    setSearchQueries((prev) => ({
                                      ...prev,
                                      [section.id]: e.target.value,
                                    }))
                                  }
                                  className="block w-full rounded-md border bg-white/20 py-1.5 pl-6 pr-3 focus:bg-white focus:text-gray-900 focus:ring-0 focus:placeholder:text-gray-500 sm:text-sm sm:leading-6"
                                />
                              </div>
                            )}
                            <div className="mb-2 pb-2 border-b border-gray-100 shrink-0">
                              <button
                                type="button"
                                onClick={() => handleToggleAll(section)}
                                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                              >
                                {isAllSelected(section)
                                  ? "Unselect All"
                                  : "Select All"}
                              </button>
                            </div>
                            <div className="overflow-y-auto overflow-x-hidden pr-1 flex-1 min-h-0 max-h-60">
                              {section.isV2 ? (
                                <div className="space-y-2.5 pb-1">
                                  {filteredOptions.map((option, optionIdx) => {
                                    if (option.isGroupHeader) {
                                      const groupMembers =
                                        option.groupMembers || [];
                                      const selectedInGroup =
                                        groupMembers.filter((m) =>
                                          selectedIds[section.id]?.includes(
                                            v2NameToId[m]
                                          )
                                        );
                                      const allSelected =
                                        groupMembers.length > 0 &&
                                        selectedInGroup.length ===
                                          groupMembers.length;
                                      const someSelected =
                                        selectedInGroup.length > 0 &&
                                        !allSelected;
                                      return (
                                        <div
                                          key={`d-v2-grp-${section.id}-${optionIdx}`}
                                          className="pt-3 pb-1 first:pt-0"
                                        >
                                          <span
                                            className={`text-[11px] font-semibold uppercase tracking-wider select-none cursor-pointer transition-colors ${allSelected || someSelected ? "text-indigo-600" : "text-gray-400 hover:text-gray-600"}`}
                                            onClick={() =>
                                              handleV2Checkbox(
                                                section,
                                                option.value,
                                                !allSelected
                                              )
                                            }
                                            onKeyDown={(e) => {
                                              if (
                                                e.key === "Enter" ||
                                                e.key === " "
                                              ) {
                                                e.preventDefault();
                                                handleV2Checkbox(
                                                  section,
                                                  option.value,
                                                  !allSelected
                                                );
                                              }
                                            }}
                                            role="button"
                                            tabIndex={0}
                                          >
                                            {option.label}
                                          </span>
                                        </div>
                                      );
                                    }
                                    const indentPx =
                                      (option.depth || 0) * 16;
                                    const showNew = isNewServiceCategory(
                                      option.value,
                                      serviceCategoryV2SourceData
                                    );
                                    const isChecked =
                                      selectedIds[section.id]?.includes(
                                        option.rfiId
                                      ) || false;
                                    return (
                                      <div
                                        key={`d-v2-${section.id}-${optionIdx}`}
                                        className="flex items-start gap-3"
                                        style={{
                                          paddingLeft: `${indentPx}px`,
                                        }}
                                      >
                                        <input
                                          value={option.value}
                                          checked={isChecked}
                                          onChange={(e) =>
                                            handleV2Checkbox(
                                              section,
                                              option.value,
                                              e.target.checked
                                            )
                                          }
                                          id={`filter-${section.id}-${optionIdx}`}
                                          name={`${section.id}[]`}
                                          type="checkbox"
                                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <label
                                          htmlFor={`filter-${section.id}-${optionIdx}`}
                                          className="text-sm font-medium text-gray-900 leading-relaxed cursor-pointer flex-1"
                                        >
                                          {option.label}
                                          {showNew && false && (
                                            <span className="ml-1.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold text-violet-600 ring-1 ring-inset ring-violet-400/60 align-middle">
                                              New
                                            </span>
                                          )}
                                        </label>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  {filteredOptions.map((option, optionIdx) => {
                                    const value = getOptionValue(
                                      section.id,
                                      option
                                    );
                                    return (
                                      <div
                                        key={`d-${section.id}-${optionIdx}-${value}`}
                                        className="flex items-center"
                                      >
                                        <div className="group grid size-4 grid-cols-1">
                                          <input
                                            value={value}
                                            checked={isSelected(
                                              section.id,
                                              value
                                            )}
                                            onChange={(e) =>
                                              handleCheckbox(
                                                section.id,
                                                option,
                                                e.target.checked
                                              )
                                            }
                                            id={`filter-${section.id}-${optionIdx}`}
                                            name={`${section.id}[]`}
                                            type="checkbox"
                                            className="col-start-1 row-start-1 size-4 appearance-none rounded-sm border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                          />
                                          <svg
                                            viewBox="0 0 16 16"
                                            fill="white"
                                            className="pointer-events-none col-start-1 row-start-1 size-4 opacity-0 group-has-[input:checked]:opacity-100"
                                          >
                                            <path d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z" />
                                          </svg>
                                        </div>
                                        <label
                                          htmlFor={`filter-${section.id}-${optionIdx}`}
                                          className="ml-3 whitespace-nowrap pr-6 text-sm font-medium text-gray-900"
                                        >
                                          {renderOptionLabel(section, option)}
                                        </label>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        </PopoverPanel>
                      </Popover>
                    );
                  })}
                </PopoverGroup>
              </div>
            </div>
        </div>
      </div>
    </div>
  );
}
